<template>
  <div id="threshold-list">
    <div class="float-right">
      <v-btn
        color="secondary"
        @click="() => { touched && !updated ? cancelEdit() : $emit('cancel-edit') }"
      >
        {{ updated ? "Back" : "Cancel" }}
      </v-btn>
      <v-btn
        :color="updated ? 'success' : 'primary'"
        :disabled="(updating || hasErrors || hasEmpty || !touched)"
        :loading="updating"
        :style="updated && 'cursor: default'"
        @click="() => { !updated && confirmUpdate() }"
      >
        <v-icon
          v-if="updated"
          size="14"
          class="mr-2"
        >
          $faCheck
        </v-icon>
        {{ updated ? 'Updated' : 'Update' }}
      </v-btn>
    </div>
    <div style="clear:both" />
    <div class="mt-5">
      <v-expansion-panels accordion>
        <v-expansion-panel
          v-for="(activityZoneSettings, activityZoneID) in thresholdsByActivityZone"
          :key="activityZoneID"
        >
          <v-expansion-panel-header hide-actions>
            <template v-slot:default="{ open }">
              <v-icon
                size="16"
                color="black"
                class="mx-4"
                style="flex:none;"
              >
                {{ open ? '$faChevronDown' : '$faChevronUp' }}
              </v-icon>
              <div class="activity-zone__name">
                {{ activityZoneSettings.name }}
              </div>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <template v-if="activityZoneSettings.thresholds.length > 0">
              <template v-for="(setting, settingId) in activityZoneSettings.thresholds">
                <edit-activity-zone-thresholds
                  :key="settingId"
                  v-bind.sync="activityZoneSettings.thresholds[settingId]"
                  :errors="errors[activityZoneID][settingId]"
                  @remove-threshold="handleRemoveThreshold(activityZoneID, settingId)"
                  @touched="validateSettings(activityZoneID)"
                />
              </template>
            </template>
            <template v-else>
              <h2>There are no thresholds in this zone.</h2>
              <div class="subtitle">
                Add thresholds to receive alerts for this zone.
              </div>
            </template>
            <v-btn
              text
              color="muted"
              class="icon-btn mt-5"
              @click="addThresholdSetting(activityZoneID)"
            >
              <v-icon class="mr-2">
                $faPlus
              </v-icon>
              <span>Add Threshold</span>
            </v-btn>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'
  import EditActivityZoneThresholds from './EditActivityZoneThresholds'
  import { mapGetters } from 'vuex'

  export default {
    name: 'EditThresholds',
    components: {
      EditActivityZoneThresholds,
    },
    props: {
      activityZonesList: Array,
    },
    data () {
      return {
        submitted: false,
        updating: false,
        profile: null,
        nudge: 0,
        errors: {},
        thresholdsByActivityZone: {},
        savedThresholdsByActivityZone: {},
        loading: true,
      }
    },
    computed: {
      ...mapGetters({
        savedThresholdProfile: 'Properties/savedThresholdProfile',
        zones: 'ActivityZones/zones',
        propertyDetails: 'Properties/propertyDetails',
      }),
      updated () {
        return this.submitted && !this.touched
      },
      touched () {
        return (
          this.hasTouchedThresholds ||
          this.nudge !== 0 ||
          (this.profile !== this.savedThresholdProfile)
        )
      },
      hasTouchedThresholds () {
        return JSON.stringify(this.savedThresholdsByActivityZone) !== JSON.stringify(this.thresholdsByActivityZone)
      },
      activityZonesTouched () {
        if (!this.hasTouchedThresholds) return []
        const touchedZones = new Set()
        Object.keys(this.thresholdsByActivityZone).forEach((activityZoneID) => {
          if (this.savedThresholdsByActivityZone[activityZoneID] &&
            JSON.stringify(this.thresholdsByActivityZone[activityZoneID].thresholds) !== JSON.stringify(this.savedThresholdsByActivityZone[activityZoneID].thresholds)) {
            touchedZones.add(activityZoneID)
          }
        })
        return [...touchedZones]
      },
      propertyId () {
        return this.$route.params.id
      },
      propertyTimezone () {
        return this.propertyDetails.timezone
      },
      hasEmpty () {
        for (const activityZoneSettings of Object.values(this.thresholdsByActivityZone)) {
          for (const setting of activityZoneSettings.thresholds) {
            if (setting.startTime === null || setting.endTime === null || setting.NRS === null) {
              return true
            }
          }
        }
        return false
      },
      hasErrors () {
        for (const activityZoneErrors of Object.values(this.errors)) {
          for (const settingError of Object.values(activityZoneErrors)) {
            if (settingError.startTime || settingError.endTime) return true
          }
        }
        return false
      },
    },
    mounted () {
      this.loading = true
      this.fetchActivityZoneThresholdsForProperty()
        .then((res) => {
          let data = res.data.data
          if (data === null) data = []
          const savedThresholdsByActivityZone = this.mapThresholdArrayToObjectByActivityZone(data)
          this.savedThresholdsByActivityZone = this.formatEditThresholdForm(savedThresholdsByActivityZone)
          const thresholdsByActivityZone = this.mapThresholdArrayToObjectByActivityZone(data)
          this.thresholdsByActivityZone = this.formatEditThresholdForm(thresholdsByActivityZone)

          this.initializeForm()
        })
      this.loading = false
    },
    methods: {
      fetchActivityZoneThresholdsForProperty () {
        const payload = {
          filter: [{ key: 'property_id', operator: '=', value: this.propertyId }],
          pagination: { perPage: 999, pageNum: 1 },
        }
        return axios.post(`${this.$store.state.baseURL}/thresholds/list`, payload)
      },
      formatEditThresholdForm (thresholdsByActivityZone) {
        return this.zones.reduce((acc, zone) => ({
          ...acc, [zone.id]: { name: zone.name, thresholds: thresholdsByActivityZone[zone.id] || [] },
        }), {})
      },
      mapThresholdArrayToObjectByActivityZone (response) {
        return response
          .sort((s1, s2) => s1.startTime - s2.startTime)
          .reduce((acc, curr) => {
            const { activityZoneID: id, startTime, endTime, NRS, timeZone } = curr
            if (acc[id]) acc[id].push({ startTime, endTime, NRS, timeZone })
            else acc[id] = [{ startTime, endTime, NRS, timeZone }]
            return acc
          }, {})
      },
      mapModelToAPIPayload (activityZoneID, thresholds) {
        const payload = { thresholds: [] }
        thresholds.forEach(({ startTime, endTime, NRS, timeZone }) => {
          activityZoneID = parseInt(activityZoneID)
          payload.thresholds.push({
            NRS,
            startTime,
            endTime,
            timeZone,
            activityZoneID,
            TriggerCount: 1,
          })
        })
        return payload
      },
      addThresholdSetting (activityZoneID) {
        this.thresholdsByActivityZone[activityZoneID].thresholds.push({ startTime: null, endTime: null, NRS: null, timeZone: this.propertyTimezone })
      },
      handleRemoveThreshold (activityZoneID, settingId) {
        this.thresholdsByActivityZone[activityZoneID].thresholds.splice(settingId, 1)
      },
      validateSettings (activityZoneID) {
        this.initializeErrors()
        const intersect = (A, B) => {
          if (B.startTime < A.startTime) return B.endTime > A.endTime
          return B.startTime < A.endTime
        }
        const sorted = this.thresholdsByActivityZone[activityZoneID].thresholds
          .slice()
          .map((s, settingId) => ({ ...s, endTime: s.endTime === 0 ? 24 : s.endTime, settingId }))
          .filter(s => s.startTime !== null && s.endTime !== null && s.NRS !== null)
          .sort((s1, s2) => s1.startTime - s2.startTime)
        sorted.reduce((prev, curr) => {
          if (intersect(prev, curr)) {
            this.errors = Object.assign({}, this.errors, {
              [activityZoneID]: {
                ...this.errors[activityZoneID],
                [curr.settingId]: { startTime: 'Conflicts with earlier setting' },
                [prev.settingId]: { endTime: 'Conflicts with later setting' },
              },
            })
          }
          return curr
        }, sorted.slice(-1))
      },
      initializeForm () {
        this.initializeErrors()
        this.nudge = 0
        this.profile = this.savedThresholdProfile
      },
      initializeErrors () {
        this.errors = Object.assign({}, this.errors, Object.keys(this.thresholdsByActivityZone).reduce((acc, curr) => ({ [curr]: {}, ...acc }), {}))
      },
      cancelEdit () {
        this.$root.$confirm({
          text: 'Your thresholds have been modified. Are you sure you want to leave without saving?',
          title: 'Leave without saving?',
          icon: '$faExclamationCircleLight',
          cancelText: 'Leave',
          confirmText: 'Stay',
          confirmColor: 'primary',
          width: 400,
          minHeight: 510,
        }).catch(() => this.$emit('cancel-edit'))
      },
      confirmUpdate () {
        this.$root.$confirm({
          text: 'Your threshold has been modified. These changes will override any existing configuration.',
          title: 'Are you sure you want to save these changes?',
          icon: '$faExclamationCircleLight',
          cancelText: 'No',
          confirmText: 'Yes',
          confirmColor: 'primary',
          width: 400,
          minHeight: 510,
        })
          .then(() => { this.updateThresholds() })
          .catch(() => {})
      },
      updateThresholds () {
        this.updating = true
        this.submitted = true
        Promise.all(this.activityZonesTouched.map((activityZoneID) => {
          const thresholds = this.thresholdsByActivityZone[activityZoneID].thresholds
          const payload = this.mapModelToAPIPayload(activityZoneID, thresholds)
          this.$store.dispatch('ActivityZones/updateThresholdsForActivityZone', { activityZoneID, payload })
            .then((res) => {
              const updatedActivityZoneThresholds = this.formatEditThresholdForm(this.mapThresholdArrayToObjectByActivityZone(res.data.data || []))
              this.savedThresholdsByActivityZone[activityZoneID].thresholds = updatedActivityZoneThresholds[activityZoneID].thresholds
            })
        }))
          .then(() => {
            const toastPayload = { type: 'success', text: 'Successfully updated thresholds' }
            this.$store.dispatch('updateMessages', toastPayload, { root: true })
            this.initializeForm()
            this.submitted = true
          })
          .finally(() => { this.updating = false })
      },
    },
  }
</script>
<style lang="scss" scoped>
.v-expansion-panel {
  &::before {
    box-shadow: unset;
    transition: unset;
  }
}
.activity-zone__name {
  flex: none;
  font-family: Degular;
  font-style: normal;
  font-weight: 600;
  font-size: 22px;
  line-height: 29px;
  display: flex;
  align-items: center;
  letter-spacing: 1px;
}
</style>
<style lang="scss">
#threshold-list {
  .label {
    padding: 10px 0;
    height: 40px;
  }
  .tooltip-caption {
    font-family: 'Degular', sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 18px;
    & > .v-icon {
      margin-left: 6px;
    }
    margin-right: 20px;
  }
  .v-select > .v-input__control > .v-input__slot {
    min-height:  40px !important;
  }
  .v-select__selection {
    font-size: 14px;
  }
  .profile-select {
    height: 40px;
    width: 161px;
  }
  .v-label {
    font-family: 'Degular', sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 18px;
    color: #535353;
    text-transform: uppercase;
  }
}
.profile-select-menu .v-list-item .v-list-item__title {
  font-size: 16px !important;
}
</style>
<style lang="scss" scoped>
.v-tooltip__content {
  line-height: 21px;
  opacity: 1 !important;
}
</style>
